import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { SELECT_EXPORT_PAGES } from "@/Constants/common";
import {
  fetchApplicantLists,
  fetchContractShifts,
  fetchTerminatedShifts,
  toggleShiftsSelectAll,
  toggleTerminatedShiftsSelectAll,
  updateApplicantsPagination,
  updateFilters,
  updateSelectedShifts,
  updateSelectedTerminatedShifts,
  updateShiftsPagination,
  updateTerminatedShiftsPagination,
  updateToggleSelected,
  deSelectAllListings,
} from "@/reducers/clientcontractdetail";
import { toastMessageText } from "@/utilities/CommonText";

import ApplicantListing from "./ApplicantListing";
import ContractListingType from "./ContractListingType";
import RunningLateMapListing from "./RunningLateMapListing";
import ShiftListing from "./ShiftListing";
import TerminatedShiftListing from "./TerminatedShiftListing";
import {
  _exportContractShifts,
  _exportContractTerminatedShifts,
} from "../../../../services/clientexport";
import ExportButton from "../ActionButtons/ExportButton";
import { SelectedCountText } from "../candidates/Listing/common/SelectedCountText";
import CalendarSearch from "../dataTables/CalendarSearch";
import TableSearch from "../dataTables/TableSearch";

/**
 * Renders the ContractListing component with various sub-components based on the active contract type.
 * Handles search functionality, updates, and pagination for shifts and applicants.
 *
 * @param {Event} e - The event object for the input change event.
 * @return {JSX.Element} The ContractListing component JSX.
 */
const ContractListing = ({ _contractID }) => {
  const {
    activeContract,
    filters,
    contractId,
    contractShiftsLists,
    contractTerminatedShiftsLists,
    shiftsPagination,
    applicantsPagination,
    terminatedShiftsPagination,
    totalShifts,
    isShiftsSelectedAll,
    selectedShifts,
    toggleSelected,
    selectedTerminatedShifts,
    totalTerminatedShifts,
    isTerminatedShiftsSelectedAll,
  } = useSelector((state) => state.clientContractDetail);
  const dispatch = useDispatch();
  const { clientUserAllPermissions } = useSelector((state) => state.session);

  const [updateShiftData, setUpdateShiftData] = useState(false);
  const [updateTerminateShiftData, setUpdateTerminateShiftData] =
    useState(false);
  useEffect(() => {
    setUpdateTerminateShiftData(!updateTerminateShiftData);
  }, [isTerminatedShiftsSelectedAll, selectedTerminatedShifts]);

  useEffect(() => {
    setUpdateShiftData(!updateShiftData);
  }, [isShiftsSelectedAll, selectedShifts]);

  /**
   * Handles the search event.
   *
   * @param {Event} e - the event object
   * @return {void}
   */
  const handleSearch = (e) => {
    const { value } = e.target;
    dispatch(updateFilters({ search: value }));
  };

  const firstRender = useRef(true);
  useEffect(() => {
    if (contractId == null || _contractID !== contractId) return;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    dispatch(deSelectAllListings());
    let newPagination = {
      currentPage: 1,
      isLastPage: false,
    };
    dispatch(
      updateShiftsPagination({
        ...shiftsPagination,
        ...newPagination,
      }),
    );
    dispatch(
      updateApplicantsPagination({
        ...applicantsPagination,
        ...newPagination,
      }),
    );
    dispatch(
      updateTerminatedShiftsPagination({
        ...terminatedShiftsPagination,
        ...newPagination,
      }),
    );
    let obj = {
      contract_id: contractId,
    };
    if (activeContract === "shifts") {
      dispatch(fetchContractShifts(obj));
    } else if (activeContract === "terminatedShifts") {
      dispatch(fetchTerminatedShifts(obj));
    } else {
      dispatch(fetchApplicantLists(obj));
    }
  }, [activeContract]);
  /**
   * A function that handles the change of shift dates.
   *
   * @param {type} dates - description of the dates parameter
   * @return {type} description of the return value
   */
  const handleDateChange = (key, dates) => {
    dispatch(updateFilters({ [key]: dates }));
  };
  return (
    <div className="ContractListing_Wrapper">
      <div className="rounded-lg bg-white px-3">
        <div className="flex justify-between gap-[20px] border-b border-b-[#E8E8E8] py-[13px]">
          <ContractListingType />
          <div className="flex grow justify-between">
            {activeContract === "shifts" ? (
              <>
                <div className="flex gap-[20px]">
                  <div className="flex h-[40px] cursor-pointer overflow-hidden rounded-lg bg-grey [&>*:last-child]:border-r-0 [&>*]:grid [&>*]:w-[56px] [&>*]:place-items-center [&>*]:border-0 [&>*]:border-r [&>*]:border-r-[#E5E6E9] [&>*]:text-txtColor">
                    <ExportButton
                      page={SELECT_EXPORT_PAGES.CONTRACT_SHIFTS}
                      // exportUrl={routingConstants.exportContractShiftList}
                      exportFunction={_exportContractShifts}
                      updateData={updateShiftData}
                      selectedTablesData={selectedShifts}
                      ignoreIds={
                        isShiftsSelectedAll
                          ? contractShiftsLists
                              .filter(
                                (ele) => !selectedShifts.includes(ele.shift_id),
                              )
                              .map((ele) => ele.shift_id)
                              .join(",")
                          : ""
                      }
                      isSelectedAll={isShiftsSelectedAll}
                      extraObj={{
                        contract_ids: selectedShifts.join(","),
                        parent_contract_id: contractId,
                      }}
                      emptyExportMessage={
                        toastMessageText.emptyExportMessageForContract
                      }
                    />
                  </div>

                  <SelectedCountText
                    selectedCount={selectedShifts.length}
                    totalCount={totalShifts}
                    isSelectedAll={isShiftsSelectedAll}
                    clearSelected={() => {
                      dispatch(updateToggleSelected(!toggleSelected));
                    }}
                    handleSelectAll={(val) => {
                      dispatch(toggleShiftsSelectAll(val));
                    }}
                    updateSelected={(val) => {
                      dispatch(updateSelectedShifts(val));
                    }}
                    currentDataLength={contractShiftsLists.length}
                  />
                </div>
                <CalendarSearch
                  val={filters.shiftDates}
                  handleChange={(dates) => {
                    handleDateChange("shiftDates", dates);
                  }}
                  highlightDates={contractShiftsLists.map((shift) =>
                    moment(shift.contract_date, "DD/MM/YYYY").format(
                      "YYYY-MM-DD",
                    ),
                  )}
                />
              </>
            ) : activeContract === "terminatedShifts" ? (
              <>
                <div className="flex gap-[20px]">
                  <div className="flex h-[40px] cursor-pointer overflow-hidden rounded-lg bg-grey [&>*:last-child]:border-r-0 [&>*]:grid [&>*]:w-[56px] [&>*]:place-items-center [&>*]:border-0 [&>*]:border-r [&>*]:border-r-[#E5E6E9] [&>*]:text-txtColor">
                    <ExportButton
                      page={SELECT_EXPORT_PAGES.CONTRACT_TERMINATED_SHIFTS}
                      // exportUrl={routingConstants.exportContractTerminatedList}
                      exportFunction={_exportContractTerminatedShifts}
                      updateData={updateTerminateShiftData}
                      selectedTablesData={selectedTerminatedShifts}
                      ignoreIds={
                        isTerminatedShiftsSelectedAll
                          ? contractTerminatedShiftsLists
                              .filter(
                                (ele) =>
                                  !selectedTerminatedShifts.includes(
                                    ele.shift_id,
                                  ),
                              )
                              .map((ele) => ele.shift_id)
                              .join(",")
                          : ""
                      }
                      isSelectedAll={isTerminatedShiftsSelectedAll}
                      extraObj={{
                        contract_ids: selectedTerminatedShifts.join(","),
                        parent_contract_id: contractId,
                      }}
                      emptyExportMessage={
                        toastMessageText.emptyExportMessageForContract
                      }
                    />
                  </div>
                  <SelectedCountText
                    selectedCount={selectedTerminatedShifts.length}
                    totalCount={totalTerminatedShifts}
                    isSelectedAll={isTerminatedShiftsSelectedAll}
                    clearSelected={() => {
                      dispatch(updateToggleSelected(!toggleSelected));
                    }}
                    handleSelectAll={(val) => {
                      dispatch(toggleTerminatedShiftsSelectAll(val));
                    }}
                    updateSelected={(val) => {
                      dispatch(updateSelectedTerminatedShifts(val));
                    }}
                    currentDataLength={contractTerminatedShiftsLists.length}
                  />
                </div>
                <CalendarSearch
                  val={filters.terminateShiftDates}
                  handleChange={(dates) => {
                    handleDateChange("terminatedShiftDates", dates);
                  }}
                  highlightDates={contractTerminatedShiftsLists.map((shift) =>
                    moment(shift.contract_date, "DD/MM/YYYY").format(
                      "YYYY-MM-DD",
                    ),
                  )}
                />
              </>
            ) : activeContract == "runningLateMap" ? (
              <></>
            ) : (
              <div className="flex grow justify-end">
                <TableSearch val={filters.search} handleChange={handleSearch} />
              </div>
            )}
          </div>
        </div>
        {activeContract !== "" ? (
          <div className="DataTable_Wrapper hide_extended_arrow mb-10 py-[20px]">
            {/* {activeContractListsType[activeContract]} */}
            {activeContract === "shifts" ? (
              <ShiftListing />
            ) : activeContract === "terminatedShifts" ? (
              <TerminatedShiftListing />
            ) : activeContract === "runningLateMap" ? (
              <RunningLateMapListing />
            ) : (
              <ApplicantListing />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ContractListing;
